import type { ReactNode } from "react";

const phases: { stage: string; title: string; description: string; icon: ReactNode }[] = [
  {
    stage: "SEED",
    title: "Prove",
    description: "Acquire 1-2 small immigration firms via equity swap (zero cash). Deploy COSX agents. Run real ops. Graduate full AI agent.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M14.31 8l5.74 9.94M9.69 8h11.48M7.38 12l5.74-9.94M9.69 16L3.95 6.06M14.31 16H2.83m13.79-4l-5.74 9.94" /></svg>
    ),
  },
  {
    stage: "SERIES A",
    title: "Scale",
    description: "Proven playbook → raise £3-5M. Acquire 10-20 firms. Deploy COSX across all. Lower cost per case unlocks new markets.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
    ),
  },
  {
    stage: "SERIES B+",
    title: "Compound",
    description: "Expand beyond legal: Financial advisory, accounting, insurance. Each vertical = new COSX skill + new market. Own or partner.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></svg>
    ),
  },
];

export function BusinessModelSlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <div className="mb-5 max-w-4xl">
        <h2 className="text-[32px] font-light tracking-tight mb-3 font-[family-name:var(--font-heading)]">
          Acquire. Transform. Compound.
        </h2>
        <p className="text-[14px] leading-relaxed text-[var(--color-fg-secondary)] font-light">
          Don&apos;t sell software to law firms — own them. Deploy COSX agents across their operations. Transform margins. Repeat across verticals.
        </p>
      </div>

      <div className="pl-5 mt-8 mb-6">
        <div className="flex flex-col gap-6">
          {phases.map((p, i) => (
            <div key={p.stage} className="relative">
              <div className="absolute left-[-17px] top-[5px] w-2.5 h-2.5 rounded-full shrink-0 z-[1] bg-[var(--color-accent)]" />
              {i < phases.length - 1 && <div className="absolute left-[-13px] top-[15px] bottom-[-28px] w-px bg-[var(--color-border)]" />}
              <div className="flex items-center gap-3 mb-1">
                <div className="text-[12px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-accent)] uppercase tracking-[0.1em]">
                  {p.stage} — {p.title}
                </div>
                <span className="text-[var(--color-fg)] shrink-0">{p.icon}</span>
              </div>
              <p className="text-[12px] leading-relaxed text-[var(--color-fg-secondary)] font-light">{p.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="text-left mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">
        <p className="text-[12px] leading-relaxed text-[var(--color-fg-secondary)] font-light">
          Lower cost per case → higher margins + lower prices → new customer segments → volume growth → <strong className="text-[var(--color-fg)] font-medium">compounding returns</strong>
        </p>
      </div>
    </div>
  );
}
